"use client";

import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import ScrollReveal from "@/components/ui/ScrollReveal";
import { useTranslations } from "next-intl";
import { trackEvent } from "@/lib/track";

const TESTIMONIAL_KEYS = [
  { quoteKey: "quote1" as const, nameKey: "name1" as const, locationKey: "location1" as const, rating: 5 },
  { quoteKey: "quote2" as const, nameKey: "name2" as const, locationKey: "location2" as const, rating: 5 },
  { quoteKey: "quote3" as const, nameKey: "name3" as const, locationKey: "location3" as const, rating: 5 },
  { quoteKey: "quote4" as const, nameKey: "name4" as const, locationKey: "location4" as const, rating: 5 },
  { quoteKey: "quote5" as const, nameKey: "name5" as const, locationKey: "location5" as const, rating: 5 },
];

const AUTO_ROTATE_INTERVAL = 5000;

export default function TestimonialCarousel() {
  const t = useTranslations("testimonials");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveIndex(index);
        setIsTransitioning(false);
      }, 300);
    },
    [isTransitioning]
  );

  const goNext = useCallback(() => {
    goTo((activeIndex + 1) % TESTIMONIAL_KEYS.length);
  }, [activeIndex, goTo]);

  const goPrev = useCallback(() => {
    goTo((activeIndex - 1 + TESTIMONIAL_KEYS.length) % TESTIMONIAL_KEYS.length);
  }, [activeIndex, goTo]);

  useEffect(() => {
    const timer = setInterval(goNext, AUTO_ROTATE_INTERVAL);
    return () => clearInterval(timer);
  }, [goNext]);

  const testimonialKey = TESTIMONIAL_KEYS[activeIndex];

  return (
    <section className="py-20 sm:py-28">
      <div className="container-luxury max-w-4xl">
        <ScrollReveal className="text-center mb-14">
          <span className="inline-block text-[11px] sm:text-xs tracking-[0.2em] uppercase font-medium text-brand-gold mb-4">
            {t("label")}
          </span>
          <h2 className="heading-display text-3xl sm:text-4xl mb-4">
            {t("title")}
          </h2>
          <p className="text-brand-muted max-w-2xl mx-auto">
            {t("subtitle")}
          </p>
        </ScrollReveal>

        <div className="relative">
          {/* Large decorative quote mark */}
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-brand-gold/10 pointer-events-none select-none">
            <svg
              className="w-24 h-24 sm:w-32 sm:h-32"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z" />
            </svg>
          </div>

          {/* Testimonial content */}
          <div className="text-center px-4 sm:px-12">
            <div
              className={cn(
                "transition-all duration-300",
                isTransitioning
                  ? "opacity-0 translate-y-2"
                  : "opacity-100 translate-y-0"
              )}
            >
              {/* Stars */}
              <div className="flex items-center justify-center gap-1 mb-6">
                {Array.from({ length: testimonialKey.rating }).map((_, i) => (
                  <Star
                    key={i}
                    className="h-4 w-4 fill-brand-gold text-brand-gold"
                  />
                ))}
              </div>

              {/* Quote */}
              <blockquote className="font-display text-xl sm:text-2xl lg:text-3xl text-brand-dark leading-relaxed mb-8">
                &ldquo;{t(testimonialKey.quoteKey)}&rdquo;
              </blockquote>

              {/* Attribution */}
              <div>
                <p className="font-medium text-brand-dark">
                  {t(testimonialKey.nameKey)}
                </p>
                <p className="text-sm text-brand-muted mt-0.5">
                  {t(testimonialKey.locationKey)}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation arrows */}
          <div className="flex items-center justify-center gap-4 mt-10">
            <button
              onClick={() => { goPrev(); trackEvent("testimonial_nav", { direction: "prev" }); }}
              className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-full border border-brand-gold/30 flex items-center justify-center text-brand-gold hover:bg-brand-gold hover:text-white hover:scale-110 active:scale-95 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 focus-visible:ring-offset-2"
              aria-label={t("previousTestimonial")}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {/* Dots */}
            <div className="flex items-center gap-2" role="tablist" aria-label={t("testimonialDots")}>
              {TESTIMONIAL_KEYS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goTo(index)}
                  role="tab"
                  aria-selected={index === activeIndex}
                  className={cn(
                    "h-2.5 min-w-[10px] rounded-full transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 focus-visible:ring-offset-2",
                    index === activeIndex
                      ? "w-6 bg-brand-gold"
                      : "w-2.5 bg-brand-gold/30 hover:bg-brand-gold/50"
                  )}
                  aria-label={t("goToTestimonial", { number: index + 1 })}
                />
              ))}
            </div>

            <button
              onClick={() => { goNext(); trackEvent("testimonial_nav", { direction: "next" }); }}
              className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-full border border-brand-gold/30 flex items-center justify-center text-brand-gold hover:bg-brand-gold hover:text-white hover:scale-110 active:scale-95 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 focus-visible:ring-offset-2"
              aria-label={t("nextTestimonial")}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
